import type { ReactNode } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import { buildPaginationHref } from '@utilities/seo/buildPaginationHref';

type PaginationProps = {
  lastPage: number;
  currentPage?: number;
};

const Pagination = ({
  lastPage,
  currentPage: currentPageProp,
}: PaginationProps) => {
  const router = useRouter();
  const pageNumber = router.query.pageNumber;
  const currentPage =
    currentPageProp ??
    Number(Array.isArray(pageNumber) ? pageNumber[0] : pageNumber || 1);
  const nextPage = currentPage + 1;
  const previousPage = currentPage - 1;

  const sharedClassName =
    'h-8 w-8 text-xs font-normal text-content-secondary flex items-center justify-center';

  const PageLink = ({
    page,
    children,
    className,
    ariaLabel,
    isCurrent,
  }: {
    page: number;
    children: ReactNode;
    className?: string;
    ariaLabel?: string;
    isCurrent?: boolean;
  }) => (
    <Link
      href={buildPaginationHref(router, page)}
      prefetch={false}
      scroll={false}
      aria-label={ariaLabel}
      aria-current={isCurrent ? 'page' : undefined}
      className={className}
    >
      {children}
    </Link>
  );

  return (
    <nav aria-label="صفحه‌بندی" className="w-full flex items-center justify-center py-4 gap-x-1">
      {currentPage > 1 ? (
        <PageLink
          page={previousPage}
          ariaLabel="صفحه قبل"
          className={classNames(sharedClassName, 'cursor-pointer rounded-[4px] border')}
        >
          <Icon name="ChevronRight" width={1.5} height={1.5} fill={colors.black} />
        </PageLink>
      ) : (
        <span
          aria-hidden
          className={classNames(
            sharedClassName,
            'rounded-[4px] bg-surface-tertiary',
          )}
        >
          <Icon
            name="ChevronRight"
            width={1.5}
            height={1.5}
            fill={colors.gray[400]}
          />
        </span>
      )}

      <PageLink
        page={1}
        ariaLabel="صفحه ۱"
        isCurrent={currentPage === 1}
        className={classNames(
          sharedClassName,
          'cursor-pointer border rounded-[4px]',
          currentPage === 1 &&
            'bg-surface-secondary border-content-primary !text-content-primary !font-medium',
        )}
      >
        1
      </PageLink>

      {currentPage > 3 && (
        <span className={classNames(sharedClassName, 'cursor-default')}>...</span>
      )}

      {currentPage > 2 && (
        <PageLink
          page={previousPage}
          ariaLabel={`صفحه ${previousPage}`}
          className={classNames(
            sharedClassName,
            'cursor-pointer border rounded-[4px]',
          )}
        >
          {previousPage}
        </PageLink>
      )}

      {currentPage > 1 && currentPage !== lastPage && (
        <PageLink
          page={currentPage}
          ariaLabel={`صفحه ${currentPage}`}
          isCurrent
          className={classNames(
            sharedClassName,
            'cursor-pointer rounded-[4px] border border-content-primary !text-content-primary !font-medium',
          )}
        >
          {currentPage}
        </PageLink>
      )}

      {currentPage === lastPage && lastPage > 1 && (
        <PageLink
          page={currentPage}
          ariaLabel={`صفحه ${currentPage}`}
          isCurrent
          className={classNames(
            sharedClassName,
            'cursor-pointer rounded-[4px] border border-content-primary !text-content-primary !font-medium',
          )}
        >
          {currentPage}
        </PageLink>
      )}

      {currentPage < lastPage - 1 && (
        <PageLink
          page={nextPage}
          ariaLabel={`صفحه ${nextPage}`}
          className={classNames(
            sharedClassName,
            'cursor-pointer border rounded-[4px]',
          )}
        >
          {nextPage}
        </PageLink>
      )}

      {currentPage < lastPage - 2 && (
        <span className={classNames(sharedClassName, 'cursor-default')}>...</span>
      )}

      {lastPage > 1 && currentPage !== lastPage && (
        <PageLink
          page={lastPage}
          ariaLabel={`صفحه ${lastPage}`}
          className={classNames(
            sharedClassName,
            'cursor-pointer border rounded-[4px]',
          )}
        >
          {lastPage}
        </PageLink>
      )}

      {currentPage < lastPage ? (
        <PageLink
          page={nextPage}
          ariaLabel="صفحه بعد"
          className={classNames(sharedClassName, 'cursor-pointer rounded-[4px] border')}
        >
          <Icon name="ChevronLeft" width={1.5} height={1.5} fill={colors.black} />
        </PageLink>
      ) : (
        <span
          aria-hidden
          className={classNames(
            sharedClassName,
            'rounded-[4px] bg-surface-tertiary',
          )}
        >
          <Icon
            name="ChevronLeft"
            width={1.5}
            height={1.5}
            fill={colors.gray[400]}
          />
        </span>
      )}
    </nav>
  );
};

export default Pagination;
